import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
const POST_TARGET = "*";
// Module-level: capture AUTH_TOKEN early (before React mounts)
// This fixes the timing issue where the Vue parent sends AUTH_TOKEN
// before VisioGateApp's useEffect listener is set up.
let pendingAuthData = null;
function parseMessagePayload(data) {
    if (data?.postis) {
        return null;
    }
    let payload = data;
    if (typeof payload === "string") {
        try {
            payload = JSON.parse(payload);
        }
        catch {
            return null;
        }
    }
    return payload;
}
if (typeof window !== "undefined") {
    const earlyListener = (event) => {
        const payload = parseMessagePayload(event.data);
        if (!payload) {
            return;
        }
        if (payload?.type === "AUTH_TOKEN" && payload?.token) {
            pendingAuthData = {
                token: payload.token,
                userInfo: payload.userInfo || null,
                customerLogo: payload.customerLogo
            };
        }
    };
    window.addEventListener("message", earlyListener);
}
function getApiBase() {
    if (typeof window === "undefined") {
        return "";
    }
    const hostname = window.location.hostname;
    // In dev (localhost/127.0.0.1), always use relative URL so the webpack
    // proxy handles routing (it reads base_url from the Referer header).
    // This avoids CORS issues with direct cross-origin fetches.
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return "";
    }
    // In prod, use base_url to call the API server directly.
    const params = new URLSearchParams(window.location.search);
    const baseUrl = params.get("base_url");
    if (baseUrl) {
        return baseUrl.replace(/\/$/, "");
    }
    return "";
}
const isUUID = (str) => typeof str === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(str);
const normalizeSessionId = (raw) => {
    if (raw == null)
        return null;
    if (typeof raw === "number")
        return raw;
    if (typeof raw === "string") {
        if (isUUID(raw))
            return raw;
        const n = Number(raw);
        if (Number.isFinite(n))
            return n;
        return raw;
    }
    return String(raw);
};
async function authorizeJoin(token, sessionId) {
    const apiBase = getApiBase();
    const url = `${apiBase}/api/visio/authorize-join`;
    const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ courseSessionId: sessionId }),
    });
    const contentType = res.headers.get("content-type") || "";
    const payload = contentType.includes("application/json") ? await res.json() : { raw: await res.text() };
    if (!res.ok) {
        const msg = payload?.reason || payload?.message || `HTTP ${res.status}`;
        const err = new Error(msg);
        err.payload = payload;
        throw err;
    }
    if (!payload?.allowed) {
        return { allowed: false, reason: payload?.reason || "Accès non autorisé" };
    }
    if (!payload?.roomName || typeof payload?.roomName !== "string") {
        return { allowed: false, reason: "roomName manquante (backend)" };
    }
    return payload;
}
function showErrorPage(errorMessage) {
    const el = document.getElementById("react");
    if (!el)
        return;
    el.innerHTML = `<div style="
      display:flex;
      flex-direction:column;
      align-items:center;
      justify-content:center;
      min-height:100vh;
      font-family:system-ui,-apple-system,sans-serif;
      background:#f5f5f5;
      margin:0;
      padding:20px;
      box-sizing:border-box;
    ">
      <div style="
        background:white;
        padding:40px;
        border-radius:12px;
        box-shadow:0 4px 20px rgba(0,0,0,0.1);
        text-align:center;
        max-width:420px;
        width:100%;
      ">
        <h1 style="color:#e74c3c;margin:0 0 16px 0;"> Erreur</h1>
        <p style="color:#666;margin:0 0 24px 0;line-height:1.5;">${errorMessage}</p>
        <button onclick="window.location.reload()" style="
          padding:12px 24px;
          background:#3498db;
          color:white;
          border:none;
          border-radius:6px;
          cursor:pointer;
          font-size:14px;
        ">
          Réessayer
        </button>
      </div>
    </div>`;
}
function postToParent(type, data = {}) {
    try {
        window.parent?.postMessage({ type, ...data, timestamp: Date.now() }, POST_TARGET);
    }
    catch {
        // no-op
    }
}
function getSessionIdFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const raw = urlParams.get("sessionId") || urlParams.get("courseSessionId");
    return normalizeSessionId(raw);
}
function getTokenFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("token") || urlParams.get("authToken");
}
function buildDisplayName(userInfo, backendResp) {
    if (userInfo?.firstName && userInfo?.lastName) {
        const formattedFirstName = userInfo.firstName.charAt(0).toUpperCase() + userInfo.firstName.slice(1).toLowerCase();
        const formattedLastName = userInfo.lastName.toUpperCase();
        return `${formattedFirstName} ${formattedLastName}`;
    }
    if (userInfo?.firstName) {
        return userInfo.firstName.charAt(0).toUpperCase() + userInfo.firstName.slice(1).toLowerCase();
    }
    if (userInfo?.lastName) {
        return userInfo.lastName.toUpperCase();
    }
    if (backendResp?.user?.name)
        return backendResp.user.name;
    if (backendResp?.user?.email)
        return String(backendResp.user.email).split("@")[0];
    return "";
}
export default function VisioGateApp() {
    const [status, setStatus] = useState("waiting");
    const [message, setMessage] = useState("En attente d'authentification...");
    const [started, setStarted] = useState(false);
    const [userName, setUserName] = useState("");
    const [autoFilled, setAutoFilled] = useState(false);
    const sessionId = useMemo(() => getSessionIdFromURL(), []);
    const processingRef = useRef(false);
    const fail = useCallback((msg) => {
        setStatus("error");
        setMessage(msg);
        postToParent("VISIO_ERROR", { message: msg });
        showErrorPage(msg);
    }, []);
    const succeed = useCallback((roomName, userInfo, backendResp) => {
        try {
            const displayName = buildDisplayName(userInfo, backendResp);
            sessionStorage.setItem("VISIO_AUTH_OK", "1");
            sessionStorage.setItem("VISIO_USER_NAME", displayName);
            // Stocker les infos utilisateur directement pour le chat
            sessionStorage.setItem("VISIO_USER_INFO", JSON.stringify({
                name: displayName,
                email: userInfo?.email || "",
                id: userInfo?.id || backendResp?.user?.id || "anonymous",
                avatar: userInfo?.avatar || ""
            }));
        }
        catch (e) { }
        postToParent("VISIO_OK", { roomName });
        setStarted(true);
    }, []);
    const processTokenAndUserInfo = useCallback(async (token, userInfo) => {
        if (processingRef.current) {
            return;
        }
        processingRef.current = true;
        if (userInfo?.firstName || userInfo?.lastName) {
            const displayName = buildDisplayName(userInfo, null);
            setUserName(displayName);
            setAutoFilled(true);
        }
        setStatus("processing");
        setMessage("Vérification en cours...");
        try {
            const backendResp = await authorizeJoin(token, sessionId);
            if (backendResp?.allowed === false) {
                return fail(backendResp?.reason || "Accès refusé");
            }
            succeed(backendResp.roomName, userInfo, backendResp);
        }
        catch (err) {
            const errorMsg = err?.payload?.reason || err?.message || "Erreur serveur";
            fail(errorMsg);
        }
    }, [fail, sessionId, succeed]);
    useEffect(() => {
        postToParent("REACT_READY");
    }, []);
    useEffect(() => {
        const tokenFromUrl = getTokenFromURL();
        if (tokenFromUrl) {
            void processTokenAndUserInfo(tokenFromUrl, null);
            return;
        }
        const tokenFromStorage = localStorage.getItem("VISIO_TOKEN");
        if (tokenFromStorage) {
            void processTokenAndUserInfo(tokenFromStorage, null);
            return;
        }
        // Check if AUTH_TOKEN was captured at module level (before React mounted)
        if (pendingAuthData) {
            postToParent("TOKEN_RECEIVED", { success: true });
            if (pendingAuthData.customerLogo) {
                try {
                    sessionStorage.setItem("VISIO_COMPANY_LOGO", pendingAuthData.customerLogo);
                }
                catch (e) { /* no-op */ }
            }
            void processTokenAndUserInfo(pendingAuthData.token, pendingAuthData.userInfo);
            pendingAuthData = null;
            return;
        }
        const handleMessage = (event) => {
            const payload = parseMessagePayload(event.data);
            if (!payload) {
                return;
            }
            if (payload?.type === "AUTH_TOKEN") {
                postToParent("TOKEN_RECEIVED", { success: true });
                const token = payload?.token;
                const userInfo = payload?.userInfo;
                const customerLogo = payload?.customerLogo;
                if (customerLogo) {
                    try {
                        sessionStorage.setItem("VISIO_COMPANY_LOGO", customerLogo);
                    }
                    catch (e) { /* no-op */ }
                }
                if (userInfo?.firstName || userInfo?.lastName) {
                    const displayName = buildDisplayName(userInfo, null);
                    setUserName(displayName);
                    setAutoFilled(true);
                }
                void processTokenAndUserInfo(token, userInfo);
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [processTokenAndUserInfo]);
    if (started) {
        return null;
    }
    return (React.createElement("div", { style: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            backgroundColor: "#1a1a1a",
            fontFamily: "system-ui, -apple-system, sans-serif",
        } },
        React.createElement("div", { style: {
                textAlign: "center",
                color: "#ffffff",
            } },
            React.createElement("div", { style: {
                    width: "40px",
                    height: "40px",
                    border: "3px solid rgba(255,255,255,0.2)",
                    borderTopColor: "#ffffff",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                    margin: "0 auto 16px",
                } }),
            React.createElement("style", null, `@keyframes spin { to { transform: rotate(360deg); } }`),
            React.createElement("p", { style: { margin: 0, fontSize: "15px", opacity: 0.85 } }, message))));
}
